'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useStytchMemberSession } from '@stytch/nextjs/b2b';
import { focusManager, useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';

import { callServerAction, isExpiredSessionError } from 'client-apis/helpers/fetch-helpers';
import { registerUnauthorizedHandler } from 'client-apis/helpers/unauthorized';
import { useSession } from 'hooks/useSession';
import { createSessionStore, SessionContext, useSessionContext } from 'store/session-store';
import { devLog } from 'utils/log';
import Sentry from 'utils/sentry';

import { showReleaseBanner } from 'components/ReleaseBanner';

import type { DecoratedSession } from 'types/infrastructure/session';

interface SessionProviderProps {
  session: DecoratedSession | undefined;
  children: React.ReactNode;
}

function SessionProviderContent({ children }: { children: React.ReactNode }) {
  const { setSession, session, logout, isLoggedIn } = useSession();
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  const previouslyLoggedInRef = useRef(false);
  const previousDeveloperIdRef = useRef(session?.developerId ?? null);
  const isLoggingOut = useSessionContext((state) => state.isLoggingOut);
  const hasNewRelease = useSessionContext((state) => state.hasNewRelease);
  const isSessionExpired = useSessionContext((state) => state.isSessionExpired);
  const setSessionExpiration = useSessionContext((state) => state.setSessionExpiration);
  const hasShownBannerRef = useRef(false);
  const handledUnauthorizedRef = useRef(false);

  const { session: stytchSession } = useStytchMemberSession();

  // Track session expiration from Stytch
  useEffect(() => {
    if (stytchSession?.expires_at) {
      setSessionExpiration(new Date(stytchSession.expires_at));
    }
  }, [stytchSession?.expires_at, setSessionExpiration]);

  // Clear caches and refetch when developerId changes (handles all paths: visibility change, refreshSession, setSession, prop sync)
  useEffect(() => {
    const currentDeveloperId = session?.developerId ?? null;
    const previousDeveloperId = previousDeveloperIdRef.current;

    if (previousDeveloperId && currentDeveloperId && previousDeveloperId !== currentDeveloperId) {
      queryClient.resetQueries();
      router.refresh(); // Re-render RSC tree so server components and suspended queries refetch with new developer
      devLog('Cleared caches for developer switch: ', previousDeveloperId, ' -> ', currentDeveloperId);
    }

    previousDeveloperIdRef.current = currentDeveloperId;
  }, [session?.developerId, queryClient, router]);

  // Ref guard because logout is not referentially stable and both 401 sources below
  // can fire for the same dead session.
  const handleUnauthorized = useCallback(() => {
    // This provider is mounted in the root layout, so it also wraps /login and
    // /authenticate. A 401 there is a failed sign-in attempt, not a dead session —
    // logging out would revoke the in-flight Stytch session and toast.remove() the
    // "Invalid credentials" message the user needs to read.
    if (!isLoggedIn) return;
    if (handledUnauthorizedRef.current) return;
    handledUnauthorizedRef.current = true;
    logout(pathname ?? undefined);
  }, [logout, pathname, isLoggedIn]);

  // Global safety net: a dead-session 401 triggers clean logout instead of
  // error boundary. Must live here because logout is only available below
  // SessionProvider, and QueryProvider is an ancestor.
  useEffect(() => {
    if (isLoggingOut) return;
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error' && isExpiredSessionError(event.action.error)) {
        handleUnauthorized();
      }
    });
    return unsubscribe;
  }, [queryClient, handleUnauthorized, isLoggingOut]);

  // Mutations run on useTransition and a raw promise, so they reach neither the query
  // cache above nor an error boundary — without this a 401 surfaces only as an error
  // toast and the user sits on a dead session. (BRIDGE-DASHBOARD-1PB)
  useEffect(() => {
    if (isLoggingOut) return;
    return registerUnauthorizedHandler(handleUnauthorized);
  }, [handleUnauthorized, isLoggingOut]);

  const updateSession = useCallback(async (): Promise<{
    success: boolean;
    developerChanged: boolean;
    didLogout: boolean;
  }> => {
    if (!isLoggedIn || isLoggingOut) return { success: false, developerChanged: false, didLogout: false };

    // If session is known to be expired, skip the API call and logout directly
    if (isSessionExpired()) {
      logout(pathname ?? undefined);
      return { success: false, developerChanged: false, didLogout: true };
    }

    try {
      const previousDeveloperId = session?.developerId ?? null;
      const newSession = await callServerAction<DecoratedSession>('session.getSession');
      setSession(newSession);
      devLog('Set session: ', newSession);

      const developerChanged = !!(
        previousDeveloperId &&
        newSession.developerId &&
        previousDeveloperId !== newSession.developerId
      );

      // Check for new release after setting session (skip during impersonation)
      if (!hasShownBannerRef.current && !developerChanged && hasNewRelease()) {
        hasShownBannerRef.current = true;
        showReleaseBanner();
      }
      return { success: true, developerChanged, didLogout: false };
    } catch (error) {
      // Expired-session 401s are an expected logout path, not a bug — mirrors the server route's handling
      if (isExpiredSessionError(error)) {
        handleUnauthorized();
        return { success: false, developerChanged: false, didLogout: true };
      }

      Sentry.captureException(error);
      console.error('Failed to update session: ', error);
      return { success: false, developerChanged: false, didLogout: false };
    }
  }, [
    setSession,
    session,
    isLoggedIn,
    logout,
    hasNewRelease,
    pathname,
    isLoggingOut,
    isSessionExpired,
    handleUnauthorized,
  ]);

  // Redirect to login page if user is not authenticated
  useEffect(() => {
    if (!isLoggingOut && previouslyLoggedInRef.current && !isLoggedIn) {
      logout(pathname ?? undefined);
    }
    // Always update the ref to track the current login state, even during logout
    previouslyLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn, logout, pathname, isLoggingOut]);

  // Handle visibility change and configure React Query focus behavior
  useEffect(() => {
    if (isLoggingOut) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // When tab becomes visible, check if session is expired before allowing refetches
        if (isSessionExpired()) {
          // Disable React Query refetching and trigger logout
          focusManager.setFocused(false);
          logout(pathname ?? undefined);
        } else {
          // Fetch session FIRST, then allow React Query refetches.
          // This ensures the developer_id cookie is current before queries refetch,
          // preventing stale data when the developer changed via spoofing in another tab.
          updateSession().then(({ developerChanged, didLogout }) => {
            if (!didLogout && document.visibilityState === 'visible' && !developerChanged) {
              // Only trigger focus-based refetches when the developer didn't change.
              // When the developer changed, the developer-change effect handles refetching
              // via resetQueries(), which clears data and triggers active observer refetches.
              focusManager.setFocused(true);
            }
          });
        }
      } else {
        // Tab is hidden, tell React Query we're not focused to prevent background refetches
        focusManager.setFocused(false);
      }
    };

    // Set initial focus state
    focusManager.setFocused(document.visibilityState === 'visible');

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Reset to default behavior on cleanup
      focusManager.setFocused(undefined);
    };
  }, [updateSession, isLoggingOut, isSessionExpired, logout, pathname]);

  return <>{children}</>;
}

export function SessionProvider({ session: initialSession, children }: SessionProviderProps) {
  const storeRef = useRef<ReturnType<typeof createSessionStore> | undefined>(undefined);

  if (!storeRef.current) {
    storeRef.current = createSessionStore(initialSession);
  }

  // Sync initialSession prop when the server re-renders with new data we care
  // about — developerId (impersonation) or session.features (dev panel toggles
  // propagating through router.refresh()).
  useEffect(() => {
    const store = storeRef.current;
    if (!store || !initialSession) return;

    const current = store.getState().session;
    const developerChanged = !!initialSession.developerId && initialSession.developerId !== current?.developerId;
    // Dev-only: the panel calls router.refresh() after each toggle. Compare by
    // value because the RSC payload yields a new object reference on every
    // refresh, but only an actual override change should update the store.
    const featuresChanged = !!current && JSON.stringify(initialSession.features) !== JSON.stringify(current.features);

    if (developerChanged || featuresChanged) {
      store.getState().setSession(initialSession);
      devLog('Synced initialSession prop: ', initialSession);
    }
  }, [initialSession]);

  return (
    <SessionContext.Provider value={storeRef.current}>
      <SessionProviderContent>{children}</SessionProviderContent>
    </SessionContext.Provider>
  );
}
